import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { DollarSign, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import PayrollPeriodSelector from './PayrollPeriodSelector';
import PayrollReviewCards from './PayrollReviewCards';
import PayrollConfirmation from './PayrollConfirmation';
import PayrollCompletion from './PayrollCompletion';
import { PayrollPeriodSettings } from '../../lib/payrollPeriodSettings';
import {
  getPayrollDataForPeriod,
  runPayroll,
  getPaystubsForRun,
  PayrollRun,
  PayrollRunItem,
  Paystub,
  ResourcePayrollData
} from '../../lib/payrollRuns';
import { sendPayrollBreakdownToAccountant } from '../../lib/payrollAccountantEmail';

interface RunPayrollModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  payrollSettings: PayrollPeriodSettings | null;
  initialPeriod?: { start: Date; end: Date } | null;
  onPayrollComplete?: () => void;
  onOpenHistory?: () => void;
}

type Step = 'period' | 'review' | 'confirm' | 'complete';

export default function RunPayrollModal({
  isOpen,
  onClose,
  businessId,
  payrollSettings,
  initialPeriod = null,
  onPayrollComplete,
  onOpenHistory
}: RunPayrollModalProps) {
  const [currentStep, setCurrentStep] = useState<Step>('period');
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoadingPeriod, setIsLoadingPeriod] = useState(false);
  
  // Period data
  const [periodStart, setPeriodStart] = useState<Date | null>(null);
  const [periodEnd, setPeriodEnd] = useState<Date | null>(null);
  
  // Payroll data
  const [payrollData, setPayrollData] = useState<Record<string, ResourcePayrollData> | null>(null);
  const [excludedResources, setExcludedResources] = useState<Set<string>>(new Set());
  
  // Processing
  const [isProcessing, setIsProcessing] = useState(false);
  const [emailToAccountant, setEmailToAccountant] = useState(false);

  const accountantEmail = payrollSettings?.accountant_email?.trim() || null;
  
  // Completed run data
  const [completedRun, setCompletedRun] = useState<PayrollRun | null>(null);
  const [completedItems, setCompletedItems] = useState<PayrollRunItem[]>([]);
  const [completedPaystubs, setCompletedPaystubs] = useState<Paystub[]>([]);

  const loadPeriodData = useCallback(async (startDate: Date, endDate: Date) => {
    setPeriodStart(startDate);
    setPeriodEnd(endDate);

    const data = await getPayrollDataForPeriod(businessId, startDate, endDate);

    if (data.summary.heldForReview > 0) {
      toast(`${data.summary.heldForReview} shift${data.summary.heldForReview === 1 ? '' : 's'} flagged for review are held out of this run until confirmed.`, { icon: '⚠️' });
    }

    if (data.summary.totalResources === 0) {
      toast.error('No unpaid payroll entries found for this period');
      setCurrentStep('period');
      setPayrollData(null);
      return;
    }

    setPayrollData(data.resources);
    setCurrentStep('review');
  }, [businessId]);

  const initialPeriodKey = useMemo(
    () =>
      initialPeriod
        ? `${format(initialPeriod.start, 'yyyy-MM-dd')}_${format(initialPeriod.end, 'yyyy-MM-dd')}`
        : null,
    [initialPeriod],
  );

  // Reset state when modal opens; auto-load when a period is pre-selected
  useEffect(() => {
    if (!isOpen) return;

    setPayrollData(null);
    setExcludedResources(new Set());
    setCompletedRun(null);
    setCompletedItems([]);
    setCompletedPaystubs([]);
    setEmailToAccountant(Boolean(payrollSettings?.accountant_email?.trim()));

    if (initialPeriod) {
      setIsLoadingPeriod(true);
      loadPeriodData(initialPeriod.start, initialPeriod.end)
        .catch((error) => {
          console.error('Error loading payroll data:', error);
          toast.error('Failed to load payroll data');
          setCurrentStep('period');
        })
        .finally(() => {
          setIsLoadingPeriod(false);
        });
    } else {
      setCurrentStep('period');
      setPeriodStart(null);
      setPeriodEnd(null);
      setIsLoadingPeriod(false);
    }
  }, [isOpen, initialPeriodKey, loadPeriodData, initialPeriod, payrollSettings?.accountant_email]);

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      onClose();
      setIsAnimating(false);
    }, 300);
  };

  const handlePeriodSelected = async (startDate: Date, endDate: Date) => {
    try {
      setIsLoadingPeriod(true);
      await loadPeriodData(startDate, endDate);
    } catch (error) {
      console.error('Error loading payroll data:', error);
      toast.error('Failed to load payroll data');
    } finally {
      setIsLoadingPeriod(false);
    }
  };

  const handleResourceRemoved = (resourceId: string) => {
    const newExcluded = new Set(excludedResources);
    newExcluded.add(resourceId);
    setExcludedResources(newExcluded);
  };

  const handleContinueToConfirm = () => {
    setCurrentStep('confirm');
  };

  const handleGoBackToReview = () => {
    setCurrentStep('review');
  };

  const handleRunPayroll = async () => {
    if (!periodStart || !periodEnd || !payrollData) return;
    
    const payrollSnapshot = Object.fromEntries(
      Object.entries(payrollData).filter(([id]) => !excludedResources.has(id))
    );
    const shouldEmailAccountant = emailToAccountant && Boolean(accountantEmail);

    try {
      setIsProcessing(true);
      
      // Get included resource IDs
      const includedResourceIds = Object.keys(payrollData).filter(
        id => !excludedResources.has(id)
      );
      
      if (includedResourceIds.length === 0) {
        toast.error('No resources selected for payroll');
        setIsProcessing(false);
        return;
      }
      
      // Run payroll
      const run = await runPayroll({
        businessId,
        periodStartDate: periodStart,
        periodEndDate: periodEnd,
        resourceIds: includedResourceIds
      });
      
      // Get the payroll run items and paystubs from the database
      const { getPayrollRunDetails } = await import('../../lib/payrollRuns');
      const runDetails = await getPayrollRunDetails(run.id);
      const paystubs = await getPaystubsForRun(run.id);
      
      setCompletedRun(run);
      setCompletedItems(runDetails.items);
      setCompletedPaystubs(paystubs);
      setCurrentStep('complete');
      
      toast.success('Payroll run completed successfully!');

      if (shouldEmailAccountant && accountantEmail) {
        const emailResult = await sendPayrollBreakdownToAccountant({
          businessId,
          accountantEmail,
          periodStart,
          periodEnd,
          runNumber: run.run_number,
          payrollData: payrollSnapshot,
        });

        if (emailResult.success) {
          toast.success('Payroll breakdown sent to accountant');
        } else {
          toast.error(emailResult.error || 'Payroll completed but failed to email accountant');
        }
      }
      
      // Notify parent component
      if (onPayrollComplete) {
        onPayrollComplete();
      }
    } catch (error) {
      console.error('Error running payroll:', error);
      const message = error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
        ? error.message
        : 'Failed to run payroll';
      toast.error(message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewHistory = () => {
    handleClose();
    if (onOpenHistory) {
      onOpenHistory();
    }
  };

  // Get active payroll data (excluding removed resources)
  const activePayrollData = payrollData
    ? Object.fromEntries(
        Object.entries(payrollData).filter(([id]) => !excludedResources.has(id))
      )
    : {};

  if (!isOpen && !isAnimating) return null;

  const stepNumber = currentStep === 'period' ? 1 : currentStep === 'review' ? 2 : currentStep === 'confirm' ? 3 : 4;
  const stepSubtitle = isLoadingPeriod
    ? 'Loading payroll data'
    : currentStep === 'period'
      ? 'Choose the pay period'
      : currentStep === 'review'
        ? 'Check each employee before continuing'
        : currentStep === 'confirm'
          ? 'This marks the selected entries as paid'
          : completedRun
            ? `Run #${completedRun.run_number.toString().padStart(4, '0')}`
            : 'Finished';

  const modalContent = (
    <>
      <div
        className="fixed inset-0 z-[100010] bg-black/50 transition-opacity duration-200"
        style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
        onClick={handleClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-[100050] flex w-full max-w-full flex-col bg-white shadow-2xl sm:w-[560px]"
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-200/60 bg-white px-4 py-3 flex-shrink-0">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50">
              <DollarSign className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-[17px] font-semibold text-gray-900">Process Payroll</h2>
              <p className="mt-0.5 text-[13px] text-gray-500">
                Step {stepNumber} of 4 · {stepSubtitle}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className={!isLoadingPeriod && currentStep === 'period' ? 'min-w-0 flex-1 overflow-hidden bg-gray-50' : 'min-w-0 flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 px-4 py-4'} style={{ WebkitOverflowScrolling: 'touch' }}>
          {isLoadingPeriod ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="animate-spin rounded-full h-10 w-10 border-3 border-gray-200 border-t-blue-600 mb-4" />
              <p className="text-[15px] text-gray-600 font-medium">Loading payroll data...</p>
            </div>
          ) : currentStep === 'period' ? (
            <PayrollPeriodSelector
              businessId={businessId}
              payrollSettings={payrollSettings}
              onPeriodSelected={handlePeriodSelected}
              onVoided={onPayrollComplete}
            />
          ) : null}

          {!isLoadingPeriod && currentStep === 'review' && payrollData && periodStart && periodEnd && (
            <PayrollReviewCards
              payrollData={activePayrollData}
              periodStart={periodStart}
              periodEnd={periodEnd}
              onResourceRemoved={handleResourceRemoved}
              onContinue={handleContinueToConfirm}
              onCancel={() => setCurrentStep('period')}
            />
          )}

          {!isLoadingPeriod && currentStep === 'confirm' && periodStart && periodEnd && (
            <PayrollConfirmation
              periodStart={periodStart}
              periodEnd={periodEnd}
              payrollData={activePayrollData}
              accountantEmail={accountantEmail}
              emailToAccountant={emailToAccountant}
              onEmailToAccountantChange={setEmailToAccountant}
              onConfirm={handleRunPayroll}
              onGoBack={handleGoBackToReview}
              isProcessing={isProcessing}
            />
          )}

          {!isLoadingPeriod && currentStep === 'complete' && completedRun && (
            <PayrollCompletion
              payrollRun={completedRun}
              payrollItems={completedItems}
              paystubs={completedPaystubs}
              onClose={handleClose}
              onViewHistory={handleViewHistory}
            />
          )}
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}

